import { useSearchParams } from 'react-router-dom';
import { Bars } from '@/components/charts/Bars';
import { Section } from '@/components/Section';
import { KPICard } from '@/components/KPICard';
import { RecountNotice } from '@/components/RecountNotice';
import { SortableTable, type SortableColumn } from '@/components/SortableTable';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import { UserLabel } from '@/components/ui/user-label';
import { useCampaignCard, useCampaignList } from '@/hooks/use-campaigns';
import { useUrlFilters } from '@/hooks/use-url-filters';
import { chartPalette } from '@/lib/chart-palette';
import type { CampaignCard, CampaignDeliveryGroup, CampaignListRow } from '@/services/api-types';
import { share } from './ContactsPage';

const utc = (value: string | null) => value ? `${new Date(value).toISOString().replace('T', ' ').replace('.000Z', '')} UTC` : '—';
const settings = (a: CampaignListRow['audienceSettings']) => a?.description ?? '—';
const listColumns: SortableColumn<CampaignListRow>[] = [
  { key: 'name', header: 'Name', sortable: true },
  { key: 'templateId', header: 'Template', sortable: true },
  { key: 'createdAt', header: 'Created', sortable: true, format: (_, r) => <>{utc(r.createdAt)}<UserLabel name={null} username={r.createdByUsername ?? r.createdBy} /></> },
  { key: 'sendDate', header: 'Sends on', sortable: true, format: (_, r) => r.sendDate ? <>{r.sendDate} {r.sendTime}<div className="text-xs text-muted-foreground">local time of each recipient</div></> : '—' },
  { key: 'audienceSettings', header: 'Audience settings', format: (_, r) => settings(r.audienceSettings) },
  { key: 'status', header: 'Status', sortable: true },
  { key: 'audience', header: 'Audience', sortable: true },
  { key: 'delivered', header: 'Delivered', sortable: true, format: (_, r) => `${r.delivered} · ${share(r.delivered, r.audience)}` },
  { key: 'pressed', header: 'Pressed', sortable: true, format: (_, r) => `${r.pressed} · ${share(r.pressed, r.delivered)}` },
  { key: 'finished', header: 'Finished', sortable: true, format: (_, r) => `${r.finished} · ${share(r.finished, r.pressed)}` },
];
const groupColumns = (statuses: CampaignCard['delivery']['statuses']): SortableColumn<CampaignDeliveryGroup>[] => [
  { key: 'key', header: 'Group', sortable: true },
  { key: 'audience', header: 'Audience', sortable: true },
  ...statuses.map(({ status, label }) => ({ key: status, header: label, sortable: true })),
];
function CampaignDetail({ data }: { data: CampaignCard }) {
  const statuses = data.delivery.statuses;
  const colours = [chartPalette.positive, chartPalette.warning, chartPalette.muted];
  const funnel: Array<Record<string, string | number>> = data.funnel.steps.map(s => ({ step: String(s.step), people: s.people, rm: s.rm, nonRm: s.nonRm,
    ...Object.fromEntries(data.funnel.entries.map((e, i) => [`entry_${i}`, s.byEntry[e.id] ?? 0])) }));
  return <div data-testid="campaign-card" className="space-y-4">
    <header className="space-y-1">
      <h2 className="text-xl font-semibold">{data.name}</h2>
      <p>{data.templateId} · {data.status}</p>
      <p>Created {utc(data.createdAt)} · {data.createdByUsername ?? data.createdBy ?? '—'}</p>
      <p>Sends on {data.sendDate ?? '—'} {data.sendTime} · local time of each recipient</p>
      <p>Audience settings: {settings(data.audienceSettings)}</p>
      <p>Launched {utc(data.launchedAt)} · {data.launchedBy ?? '—'}</p>
      {data.stoppedAt && <p>Stopped {utc(data.stoppedAt)} · {data.stoppedBy ?? '—'}</p>}
      <p>First send scheduled: {utc(data.firstRunAt)} · Last send scheduled: {utc(data.lastRunAt)}</p>
      <p>Excluded at launch: {Object.entries(data.excludedAtLaunch).map(([why, n]) => `${why}: ${n}`).join(' · ') || 'None'}</p>
    </header>
    <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <KPICard label="Audience" value={data.audience} showDelta={false} />
      {statuses.map(({ status, label }) => <KPICard key={status}
        label={label} value={data.delivery.byStatus[status]} showDelta={false} />)}
    </div>
    <Section title="Delivery reasons" testId="campaign-reasons">
      <SortableTable tableId="campaignReasons" rows={data.delivery.reasons} getRowKey={r => JSON.stringify([r.status, r.reason])}
        columns={[{ key: 'status', header: 'Status' }, { key: 'reason', header: 'Reason' }, { key: 'people', header: 'People', sortable: true }]} />
    </Section>
    <div className="grid gap-4 xl:grid-cols-3">
      {([{ title: 'By language', rows: data.delivery.byLanguage, id: 'language' }, { title: 'By source', rows: data.delivery.bySource, id: 'source' },
        { title: 'By RM', rows: data.delivery.byRole, id: 'role' }]).map(g => <Section key={g.id} title={g.title} testId={`campaign-${g.id}`}>
        <SortableTable tableId={`campaign-${g.id}`} rows={g.rows} getRowKey={r => r.key} columns={groupColumns(statuses)} />
      </Section>)}
    </div>
    <Section title="Funnel" testId="campaign-funnel">
      <p>{data.funnel.started} started · {data.funnel.finished} finished · {share(data.funnel.finished, data.funnel.started)} of started</p>
      <Bars data={funnel} categoryKey="step" categoryLabel={s => `Step ${s}`} testId="campaign-funnel-entry" emptyMessage="No presses in this campaign." series={[
        { key: 'people', name: 'All', color: chartPalette.primary },
        ...data.funnel.entries.map((e, i) => ({ key: `entry_${i}`, name: e.label, color: colours[i % colours.length] })),
      ]} />
      <Bars data={funnel} categoryKey="step" categoryLabel={s => `Step ${s}`} testId="campaign-funnel-role" emptyMessage="No presses in this campaign." series={[
        { key: 'rm', name: 'RM', color: chartPalette.primary }, { key: 'nonRm', name: 'non-RM', color: chartPalette.muted },
      ]} />
    </Section>
    <Section title="After the send" testId="campaign-wrote">
      <div className="grid gap-3 sm:grid-cols-3">
        {([{ label: 'Wrote in the 7 days before', n: data.wrote.before7d, open: null },
          { label: 'Within 24 h after', n: data.wrote.after24h, open: data.wrote.open24hUntil },
          { label: 'Within 7 days after', n: data.wrote.after7d, open: data.wrote.open7dUntil }]).map(w => <div key={w.label}>
          <KPICard label={w.label} value={`${w.n} of ${data.wrote.delivered} · ${share(w.n, data.wrote.delivered)}`} showDelta={false} />
          {w.open && <p className="text-sm text-muted-foreground">Still open until {utc(w.open)}</p>}
        </div>)}
      </div>
    </Section>
  </div>;
}
export function CampaignsPage() {
  const { filters } = useUrlFilters();
  const [params, setParams] = useSearchParams();
  const id = params.get('campaign');
  const list = useCampaignList(filters), card = useCampaignCard(filters, id);
  const active = id ? card : list;
  const select = (campaignId: string | null) => setParams(previous => {
    const next = new URLSearchParams(previous);
    if (campaignId) next.set('campaign', campaignId); else next.delete('campaign');
    return next;
  });
  return <TabErrorBoundary name="Campaigns"><section className="space-y-4" aria-labelledby="campaigns-heading">
    <header>
      <h1 id="campaigns-heading" className="text-2xl font-semibold">Campaigns</h1>
      <p data-testid="campaigns-admin-note" className="text-sm text-muted-foreground">Recipients with admin accounts are {filters.admins === 'included' ? 'included' : 'excluded'}, here as on every other tab.</p>
      <p className="text-sm text-muted-foreground">Each campaign is counted whole; the period and day filters do not apply.</p>
    </header>
    {id && <button type="button" className="text-primary underline" onClick={() => select(null)}>Back to all campaigns</button>}
    <RecountNotice queries={[active]} />
    {active.isError ? <div role="alert">Campaigns failed to load. <button type="button" className="underline" onClick={() => void active.refetch()}>Retry</button></div>
      : id ? card.data ? <CampaignDetail data={card.data} /> : !card.isLoading && card.data === null ? <p>No such campaign.</p> : <p>Loading campaign…</p>
        : <Section title="All campaigns" testId="campaigns-list">
          <SortableTable tableId="campaigns" rows={list.data ?? []} getRowKey={r => r.campaignId} columns={listColumns}
            isLoading={list.isLoading} onRowClick={r => select(r.campaignId)} emptyMessage="No campaigns yet." />
        </Section>}
  </section></TabErrorBoundary>;
}
